import { useState } from 'react';
import { Input, Typography } from 'antd';
import { SendOutlined, PhoneOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface Props {
  pendingCount: number;
  phone: string;
  onPhoneChange: (value: string) => void;
  onSend: () => void;
  sending: boolean;
}

/**
 * Invitation hero card — gradient header + telefon input + send button.
 * ProvidersPage tepasida hero sifatida turadi.
 */
export default function InvitationsHeroCard({
  pendingCount,
  phone,
  onPhoneChange,
  onSend,
  sending,
}: Props) {
  const { t } = useTranslation('owner');
  const [pressed, setPressed] = useState(false);
  const accent = '#3390ec';
  const accentDark = '#1677ff';

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 18,
        overflow: 'hidden',
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        boxShadow: 'var(--bl-shadow-soft, 0 4px 14px rgba(51,144,236,0.10))',
      }}
    >
      {/* Gradient hero header */}
      <div
        style={{
          position: 'relative',
          padding: '14px 16px 14px',
          background: `linear-gradient(135deg, ${accent}10 0%, ${accent}1c 60%, ${accent}08 100%)`,
          borderBottom: `1px solid ${accent}14`,
          overflow: 'hidden',
        }}
      >
        {/* Decorative blur orb */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -30,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `${accent}24`,
            filter: 'blur(6px)',
            opacity: 0.55,
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
              color: '#fff',
              fontSize: 17,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: `0 4px 12px ${accent}38, inset 0 1px 0 rgba(255,255,255,0.22)`,
            }}
          >
            <SendOutlined />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--bl-text-muted, #6b7280)',
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginBottom: 2,
              }}
            >
              {t('invitations.title')}
            </Text>
            <Text
              style={{
                fontSize: 13,
                color: 'var(--bl-text-muted, #6b7280)',
                display: 'block',
                lineHeight: 1.45,
              }}
            >
              {t('invitations.hint')}
            </Text>
          </div>

          {pendingCount > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                fontWeight: 700,
                color: '#d97706',
                background: 'rgba(250,140,22,0.10)',
                border: '1px solid rgba(250,140,22,0.24)',
                borderRadius: 999,
                padding: '3px 9px',
                flexShrink: 0,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#fa8c16',
                  boxShadow: '0 0 0 3px rgba(250,140,22,0.28)',
                }}
              />
              {t('invitations.pendingCount', { count: pendingCount })}
            </span>
          )}
        </div>
      </div>

      {/* Form */}
      <div style={{ padding: '14px 14px 14px', display: 'flex', gap: 8 }}>
        <Input
          prefix={<PhoneOutlined style={{ color: 'var(--bl-text-muted, #9ca3af)' }} />}
          placeholder={t('invitations.phonePlaceholder')}
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          onPressEnter={() => !sending && onSend()}
          disabled={sending}
          type="tel"
          inputMode="tel"
          style={{ borderRadius: 11, flex: 1, height: 42 }}
        />

        <button
          type="button"
          onClick={() => !sending && onSend()}
          disabled={sending}
          onTouchStart={() => !sending && setPressed(true)}
          onTouchEnd={() => setPressed(false)}
          onTouchCancel={() => setPressed(false)}
          onMouseDown={() => !sending && setPressed(true)}
          onMouseUp={() => setPressed(false)}
          onMouseLeave={() => setPressed(false)}
          style={{
            minHeight: 42,
            padding: '0 14px',
            border: '1px solid rgba(22,119,255,0.18)',
            borderRadius: 11,
            background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: sending ? 'not-allowed' : 'pointer',
            opacity: sending ? 0.85 : 1,
            boxShadow: `0 4px 12px ${accent}38, inset 0 1px 0 rgba(255,255,255,0.18)`,
            transform: pressed && !sending ? 'scale(0.97)' : 'scale(1)',
            transition: 'transform 0.1s ease, opacity 0.15s ease',
            WebkitTapHighlightColor: 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            outline: 'none',
            letterSpacing: 0.1,
            fontFamily: 'inherit',
            flexShrink: 0,
          }}
        >
          {sending ? <LoadingOutlined spin /> : <SendOutlined />}
          <span>{t('invitations.inviteBtn')}</span>
        </button>
      </div>
    </div>
  );
}
